import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Globe, Heart, Landmark, Users, Target, Award } from 'lucide-react';
import '../styles/cultural-platform.css';

const AboutEnhanced = () => {
  const observerRef = useRef(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.scroll-reveal').forEach((el) => {
      observerRef.current.observe(el);
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const values = [
    {
      icon: Globe,
      title: 'Global Unity',
      description: 'Uniting the world through arts, films and culture under one roof'
    },
    {
      icon: Heart,
      title: 'Cultural Diplomacy',
      description: 'Promoting international cooperation through creative expression'
    },
    {
      icon: Landmark,
      title: 'National Identity',
      description: 'Strengthening positive national identity through constructive expression'
    },
    {
      icon: Users,
      title: 'Collaborative Spirit',
      description: 'Working with governments and world leaders for global impact'
    },
    {
      icon: Target,
      title: 'UN SDG Alignment',
      description: 'Advancing Sustainable Development Goals through creative means'
    },
    {
      icon: Award,
      title: 'Strategic Soft Power',
      description: 'Leveraging arts and culture as instruments of peaceful influence'
    }
  ];

  return (
    <div className="about-enhanced-page" data-testid="about-page">
      {/* Hero Section */}
      <section style={{
        background: 'linear-gradient(135deg, #F8F9FA 0%, #FFFFFF 100%)',
        padding: '100px 24px 80px',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <h1 className="cultural-hero-title" style={{ marginBottom: '32px' }}>
            About Project Soft Power
          </h1>
          <p style={{ 
            fontSize: '20px',
            color: 'var(--text-secondary)',
            lineHeight: '1.8',
            maxWidth: '900px',
            margin: '0 auto'
          }}>
            An International Independent Organization leveraging arts, films, culture, and creative expression as strategic soft power—strengthening national identity, cultural influence, and international collaboration.
          </p>
        </div>
      </section>

      {/* Main Mission Statement */}
      <section style={{ 
        background: 'var(--primary-white)',
        padding: '80px 24px'
      }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <div className="scroll-reveal" style={{
            background: 'var(--surface-light)',
            padding: '48px',
            borderRadius: '12px',
            borderLeft: '4px solid var(--accent-blue)'
          }}>
            <p style={{
              fontSize: '18px',
              color: 'var(--text-primary)',
              lineHeight: '1.9',
              marginBottom: '24px'
            }}>
              Project Soft Power is an International Independent Organization founded by <strong>Leander Lee Marty</strong>, leveraging arts, films, culture, and creative expression as strategic soft power—strengthening national identity, cultural influence, and international collaboration.
            </p>
            <p style={{
              fontSize: '18px',
              color: 'var(--text-primary)',
              lineHeight: '1.9',
              marginBottom: '24px'
            }}>
              Project Soft Power works along with the governments and world leaders to strengthen 
              positive national identity through constructive expression. It stands to perfectly 
              align with a nation's objectives, particularly in terms of industry development, 
              talent engagement, and the potential economic and cultural impact.
            </p>
            <p style={{
              fontSize: '18px',
              color: 'var(--text-primary)',
              lineHeight: '1.9'
            }}>
              Project Soft Power breathes and lives on the core values of the United Nations and 
              acts as an inspirational wing, uniting the world through arts, films and culture 
              under one roof. Our work actively advances UN goals, specifically the Sustainable 
              Development Goals (SDGs) through creative means, initiatives that promote cultural 
              diplomacy, international cooperation, and unity.
            </p>
          </div>
        </div>
      </section>

      {/* Tagline Section */}
      <section style={{
        background: 'var(--text-primary)',
        color: 'var(--primary-white)',
        padding: '80px 24px',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <div className="cultural-tagline" style={{ 
            color: 'var(--primary-white)',
            fontSize: '32px',
            fontWeight: '400',
            lineHeight: '1.6'
          }}>
            <div>One world.</div>
            <div>Many nations.</div>
            <div>One shared responsibility.</div>
          </div>
        </div>
      </section>

      {/* Welcome Section */}
      <section style={{
        background: 'var(--surface-light)',
        padding: '80px 24px',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <h2 className="cultural-section-title scroll-reveal" style={{ 
            marginBottom: '24px',
            fontSize: '36px'
          }}>
            Welcome to Project Soft Power
          </h2>
          <p style={{
            fontSize: '18px',
            color: 'var(--text-secondary)',
            fontStyle: 'italic'
          }}>
            Founded by Leander Lee Marty
          </p>
        </div>
      </section>

      {/* Core Values */}
      <section style={{ 
        background: 'var(--primary-white)',
        padding: '80px 24px'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <h2 className="cultural-section-title scroll-reveal">
              Our Core Values
            </h2>
            <p className="cultural-section-description scroll-reveal" style={{ marginTop: '12px', textAlign: 'center', margin: '12px auto 0' }}>
              The principles that guide our mission for global cultural unity
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '24px'
          }}>
            {values.map((value, index) => (
              <div
                key={index}
                className="cultural-card scroll-reveal"
                style={{
                  padding: '32px',
                  textAlign: 'center',
                  animationDelay: `${index * 0.1}s`
                }}
              >
                <div style={{
                  display: 'inline-flex',
                  padding: '16px',
                  borderRadius: '12px',
                  background: 'var(--accent-blue)',
                  color: 'var(--primary-white)',
                  marginBottom: '20px'
                }}>
                  <value.icon size={28} />
                </div>
                <h3 style={{
                  fontFamily: 'var(--font-primary)',
                  fontSize: '18px',
                  fontWeight: '500',
                  marginBottom: '12px',
                  color: 'var(--text-primary)'
                }}>
                  {value.title}
                </h3>
                <p style={{
                  fontSize: '14px',
                  color: 'var(--text-secondary)',
                  lineHeight: '1.6'
                }}>
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={{
        background: 'linear-gradient(135deg, var(--accent-blue) 0%, #1557B0 100%)',
        color: 'var(--primary-white)',
        padding: '80px 24px',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <h2 style={{
            fontFamily: 'var(--font-primary)',
            fontSize: '32px',
            fontWeight: '400',
            marginBottom: '20px'
          }}>
            Partner With Us
          </h2>
          <p style={{
            fontSize: '18px',
            opacity: 0.9,
            marginBottom: '32px',
            lineHeight: '1.7'
          }}>
            Join our global network of governments, cultural institutions, and creative leaders 
            committed to peace through arts and cultural diplomacy.
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link
              to="/contact"
              data-testid="about-contact-link"
              style={{
                padding: '12px 32px',
                background: 'var(--primary-white)',
                color: 'var(--accent-blue)',
                borderRadius: '4px',
                textDecoration: 'none',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              Contact Us
            </Link>
            <Link
              to="/team"
              data-testid="about-team-link"
              style={{
                padding: '12px 32px',
                background: 'transparent',
                color: 'var(--primary-white)',
                border: '1px solid rgba(255,255,255,0.5)',
                borderRadius: '4px',
                textDecoration: 'none',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              Meet Our Team
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutEnhanced;
